import { API } from '..';

export interface ComposeResult {
  id: string;
  url: string;
  thumbnailUrl?: string;
  duration: number;
  status: string;
  error?: string;
}

export interface JobStatus {
  jobId: string;
  status: 'Processing' | 'Done' | 'Failed';
  progress: number;
  stage: string;
  error?: string;
  result?: ComposeResult;
}

// Render NỀN: gửi clip + kịch bản → nhận jobId ngay (không chờ treo request).
export const startCompose = (formData: FormData) =>
  API.post<{ jobId: string; status: string }>('/v1/media-service/video/compose', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);

// Poll tiến độ/kết quả (POST vì gateway media chỉ mở POST).
export const getJobStatus = (jobId: string) =>
  API.post<JobStatus>(`/v1/media-service/video/job-status?jobId=${jobId}`).then((r) => r.data);

// Highlight AI: gửi 1 clip → gợi ý các khoảng khoảnh khắc hấp dẫn (giây) theo độ to audio.
export interface HighlightRange { start: number; end: number }
export const getHighlights = (file: File) => {
  const fd = new FormData();
  fd.append('file', file, file.name);
  return API.post<HighlightRange[]>('/v1/media-service/video/highlights', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((r) => r.data);
};

// Kho nhạc free Cab cung cấp (Pixabay, royalty-free).
export interface MusicTrack {
  id: string;
  title: string;
  mood: string;
  category: string; // KHOÁ danh mục (khớp MusicCategory.key) để lọc theo nhóm
  isSfx: boolean;
  author: string;
  source: string;
  credit?: string | null; // ghi công bắt buộc (CC-BY); null nếu không cần
  isVip?: boolean; // nhạc cao cấp — chỉ tài khoản CAB+ dùng được khi xuất video
}

export interface MusicCategory {
  key: string;
  label: string;
}

export interface MusicLibraryResponse {
  categories: MusicCategory[];
  tracks: MusicTrack[];
}

export const getMusicLibrary = () =>
  API.post<MusicLibraryResponse>('/v1/media-service/video/music-library').then(
    (r) => r.data
  );

// Voice AI (CAB+): text → giọng đọc (mp3). Trả base64 → dựng Blob để nghe thử + ghép vào video.
export interface VoiceOption { id: string; label: string }
export const VOICE_OPTIONS: VoiceOption[] = [
  { id: 'vi-VN-Wavenet-A', label: 'Nữ – Ngọc (miền Bắc)' },
  { id: 'vi-VN-Wavenet-D', label: 'Nữ – Mai (trầm ấm)' },
  { id: 'vi-VN-Wavenet-B', label: 'Nam – Bình (miền Bắc)' },
];
export const synthesizeVoice = (text: string, voice: string, speed: number) =>
  API.post<{ audioContent: string; chars: number; remaining: number }>('/v1/media-service/video/tts', { text, voice, speed }).then((r) => {
    const bin = atob(r.data.audioContent);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { blob: new Blob([bytes], { type: 'audio/mpeg' }), remaining: r.data.remaining };
  });

// Hạn mức Voice AI: còn bao nhiêu ký tự (free tháng + CAB+ + gói mua) + danh sách gói.
export interface VoicePack { id: string; label: string; chars: number; priceCa: number }
export interface VoiceQuota {
  isPremium: boolean;
  freePerMonth: number;
  cabPlusPerMonth: number;
  monthlyAllowance: number;
  monthlyUsed: number;
  monthlyRemaining: number;
  packChars: number;
  totalRemaining: number;
  maxCharsPerRequest: number;
  balanceCa: number;
  resetAt: string | null;
  packs: VoicePack[];
}
export const getVoiceQuota = () =>
  API.get<VoiceQuota>('/v1/payment-service/voice/quota').then((r) => r.data);

export const buyVoicePack = (packId: string, refUserId?: string) =>
  API.post<{ success: boolean; message: string; packChars: number; balanceCa: number }>(
    `/v1/payment-service/voice/buy-pack?packId=${encodeURIComponent(packId)}${refUserId ? `&refUserId=${encodeURIComponent(refUserId)}` : ''}`
  ).then((r) => r.data);

// Nghe thử 1 bài (POST vì gateway media chỉ mở POST) → blob url để phát trên trình duyệt.
export const getMusicPreviewUrl = (id: string) =>
  API.post(`/v1/media-service/video/music-preview?id=${id}`, null, {
    responseType: 'blob',
  }).then((r) => URL.createObjectURL(r.data as Blob));
